import { atom } from 'jotai';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';

import type { SectionOverlaySetting } from '@/lib/stationVolumes/sectionOverlays';

export const DOCK_HEIGHT_DEFAULT = 280;
export const DOCK_HEIGHT_MIN = 180;
export const DOCK_HEIGHT_MAX = 560;
/** Below this the two chart panes collapse to the bar pane only. */
export const DOCK_NARROW_PX = 760;
/** Below this the dock forces the Table tab. */
export const DOCK_VERY_NARROW_PX = 520;

export type StationVolumeTab = 'chart' | 'table' | 'section' | 'profile' | 'strip';

/**
 * Which analysis the open dock is showing. `'volumes'` is the station-volumes
 * dock (`StationVolumeDock`), `'clearance'` the clearance dock
 * (`ClearanceDock`). One field rather than a second atom, because "only one
 * dock at a time" is a real invariant — comparing two runs is a v2 feature and
 * a half-built comparison is worse than none — and `stationDockOpenAtom`, the
 * viewer's hotkeys and the walk toolbar's `[` / `]` guard all keep working
 * unchanged.
 */
export type StationDockDataset = 'volumes' | 'clearance';

// T8.2: the dock's resize height is a per-user preference, not per-layer or
// per-session UI state, so it lives in localStorage (see the atomWithStorage
// + createJSONStorage pattern at SurfaceDifferencePanel.tsx:3,39-41) rather
// than inside `stationVolumeDockAtom` below.
//
// This atom stores whatever it is given; the clamp to
// [DOCK_HEIGHT_MIN, DOCK_HEIGHT_MAX] lives in `stationVolumeDockAtom`'s
// setter, which is the only writer any component uses. Write here directly
// and nothing clamps for you.
const dockHeightStorage = createJSONStorage<number>(() => localStorage);
export const stationVolumeDockHeightAtom = atomWithStorage<number>(
  '@actegon.gis.stationVolumes.dockHeight',
  DOCK_HEIGHT_DEFAULT,
  dockHeightStorage,
);

/**
 * Whether the open dock fills the viewer region instead of sitting in its
 * bottom strip.
 *
 * A SEPARATE atom rather than a field on `stationVolumeDockAtom`, for the same
 * reason the height is one: that atom's setter is called by half a dozen
 * writers (the chips, both docks' tab handlers, the resize grip), and adding a
 * required field to its shape would make every one of them restate a value it
 * has no opinion about.
 *
 * Persisted like the height — it is a per-session reading preference, and a
 * reader who expanded the dock to study a section wants it expanded when they
 * open the next one. Shared by BOTH docks deliberately: they occupy the same
 * slot and only one is ever mounted (ruling R2), so one flag cannot be
 * ambiguous.
 */
export const stationVolumeDockExpandedAtom = atomWithStorage<boolean>(
  '@actegon.gis.stationVolumes.dockExpanded',
  false,
  createJSONStorage<boolean>(() => localStorage),
);

/**
 * Whether the header's PARAMETER chips are shown (station interval, near-miss
 * bands, offset convention, and their siblings in the other dock).
 *
 * Off by default. Finding M9 put those settings in the header for a good
 * reason — a figure whose settings you cannot see cannot be checked or
 * reproduced — but they are reference material, read once and then in the way
 * on every subsequent glance ("I dont always need ... Or at least they could
 * be smaller!"). A disclosure keeps them one click from the figures they
 * qualify instead of deleting them.
 *
 * WARNING chips are deliberately NOT behind this flag — "the alignment has
 * changed since this run" is not a parameter, it is a reason to distrust every
 * number beside it, and hiding it by default would be exactly the silent
 * failure the amber tone exists to prevent.
 */
export const stationVolumeDockParamsVisibleAtom = atomWithStorage<boolean>(
  '@actegon.gis.stationVolumes.dockParams',
  false,
  createJSONStorage<boolean>(() => localStorage),
);

/**
 * The strip map's tolerance band, in METRES, per analysed layer (strip-map
 * spec §3.2). Only consulted when the run carries no expectation of its own —
 * a run that has tolerances shows those, read-only, because the run's
 * tolerance is the tolerance.
 *
 * `null` is the DEFAULT (±20 mm, A-2); `0` is the user having switched the
 * band off, and the two must stay distinguishable — an `atomWithStorage`
 * defaulting to `0.02` could not tell "never touched" from "set to 20 mm".
 */
export const stripToleranceMAtom = atomFamily((layerId: string) =>
  atomWithStorage<number | null>(
    `@actegon.gis.stationVolumes.stripTolerance.${layerId}`,
    null,
    createJSONStorage<number | null>(() => localStorage),
  ),
);

/** The strip map's ramp half-span override, in metres, per analysed layer.
 *  `null` means Auto — p98 of the finite |Δz| over the run's CELLS, by the
 *  same interpolation `aggregate.ts::symmetricDomain` runs for the 3D bands
 *  over a different quantity (|net m³/m| per bin). Same rule, not the same
 *  number (spec §3.3). */
export const stripSpanMAtom = atomFamily((layerId: string) =>
  atomWithStorage<number | null>(
    `@actegon.gis.stationVolumes.stripSpan.${layerId}`,
    null,
    createJSONStorage<number | null>(() => localStorage),
  ),
);

/** The layerId/tab/dataset half of the open dock — see `stationVolumeDockAtom`. */
const dockBaseAtom = atom<{ layerId: string; tab: StationVolumeTab; dataset: StationDockDataset } | null>(null);

/**
 * The open dock, keyed by derived-layer id. Only one dock at a time —
 * comparing two runs is a v2 feature and a half-built comparison is worse
 * than none (spec §7.8).
 *
 * This is a **derived WritableAtom**, not a plain `PrimitiveAtom`: reading it
 * always merges the live `layerId`/`tab` with the current persisted height
 * from `stationVolumeDockHeightAtom`, and writing it (only ever with a full
 * `{ layerId, tab, height } | null` object literal — there is no partial
 * update) clamps `height` to `[DOCK_HEIGHT_MIN, DOCK_HEIGHT_MAX]` and persists
 * it to `stationVolumeDockHeightAtom` (localStorage) so the preference
 * survives closing the dock and carries over to the next layer's dock.
 */
export const stationVolumeDockAtom = atom(
  (get) => {
    const base = get(dockBaseAtom);
    return base && { ...base, height: get(stationVolumeDockHeightAtom) };
  },
  (
    _get,
    set,
    next: { layerId: string; tab: StationVolumeTab; height: number; dataset?: StationDockDataset } | null,
  ) => {
    // `dataset` defaults to 'volumes' so every writer that predates the
    // clearance dock (StationVolumesChip, StationVolumeDock's own resize and
    // tab handlers) keeps its exact behaviour without a change.
    set(dockBaseAtom, next && { layerId: next.layerId, tab: next.tab, dataset: next.dataset ?? 'volumes' });
    if (next) {
      set(stationVolumeDockHeightAtom, Math.max(DOCK_HEIGHT_MIN, Math.min(DOCK_HEIGHT_MAX, next.height)));
    }
  },
);

/**
 * Whether the dock is open, and nothing else.
 *
 * `stationVolumeDockAtom` carries the live height, which the resize drag
 * rewrites on every `pointermove`; a component that only needs "is the dock
 * up?" (`Viewer`'s hotkey wiring, `AlignmentWalkToolbar`'s `[` / `]` guard)
 * subscribes here instead, so a drag re-renders neither of them.
 */
export const stationDockOpenAtom = atom((get) => get(dockBaseAtom) !== null);

/** Brush range as BIN INDICES — survives re-render and window resize. */
export const stationVolumeSelectionAtom = atom<{
  layerId: string;
  fromBin: number;
  toBin: number;
} | null>(null);

/** Transient chart <-> table <-> 3D hover link. Never persisted. */
export const stationVolumeHoverBinAtom = atom<number | null>(null);

/** 3D corridor bands per derived layer. */
export const stationBandsVisibleAtom = atomFamily((_layerId: string) => atom(true));

/**
 * "Clip to station" (spec §11.1), per analysed layer. Default OFF: it takes
 * over the user's clip box, which is a shared piece of viewer state, so it has
 * to be asked for.
 */
export const stationClipToBinAtom = atomFamily((_layerId: string) => atom(false));

/**
 * The RESOLVED section-overlay settings for the layer the dock is open on
 * (section-overlays spec §5.3) — what the 3D section reads to draw the same
 * reference lines the 2D chart draws.
 *
 * Published by `StationVolumeDock` and by nothing else, because resolving the
 * list is a three-way adjudication it alone can make: the per-browser settings
 * atom, the settings QUERY, and the `settingsHash` the worker stamped on the
 * layer. A second copy of that logic in the scene would agree with this one
 * everywhere except where they disagree — which is the only case that matters.
 *
 * `null` whenever no dock is open. The `layerId` rides along so a consumer can
 * refuse a list that belongs to a different run: the scene and the dock are
 * mounted independently and there is one frame, on every dock switch, where
 * the two do not yet name the same layer.
 */
export const sectionOverlaySettingsAtom = atom<{
  layerId: string;
  settings: readonly SectionOverlaySetting[];
} | null>(null);

/** Dialog-time swath preview; null whenever the dialog is closed. */
export const stationVolumePreviewAtom = atom<{
  key: string;
  swathLeft: number;
  swathRight: number;
  from: number | null;
  to: number | null;
} | null>(null);
